import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useWorkspaceUser } from '@/hooks/api/0151_workspace_user';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { useUpdateUser } from '@/hooks/api/0490_update_user';
import { toastSuccess } from '@/lib/toast';
import { useWorkspaceUsersInvalidate } from '@/hooks/api/0150_workspace_users';

type FormInputs = {
  identifier: string;
  id: number;
  admin: boolean;
  invalidity: boolean;
};

export const WorkspaceUserEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('ユーザー権限編集'), []);
  useEffect(
    () => setBreadcrumbs([{ label: `スペースメンバー設定`, path: `/w/${_w_!.identifier}/setting/members` }]),
    []
  );

  const navigate = useNavigate();
  const { id } = useParams();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const { data: workspaceUser, isError } = useWorkspaceUser({ identifier: _w_!.identifier, id: Number(id!) });
  const { mutate, data, isPending, error } = useUpdateUser();
  const { invalidate } = useWorkspaceUsersInvalidate(_w_!.identifier);
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: { identifier: _w_!.identifier, id: Number(id!) },
  });

  useEffect(() => {
    if (isError) {
      navigate(`/w/${_w_!.identifier}/setting/members`);
    }
  }, [isError]);

  useEffect(() => {
    if (workspaceUser) {
      setValue('admin', workspaceUser.admin);
      setValue('invalidity', workspaceUser.invalidity);
    }
  }, [workspaceUser]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('ワークスペースメンバーを編集しました');
      invalidate(); // UserInvitationsのキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting/members`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (workspaceUser == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'member'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">ユーザー権限編集</h5>
                </div>
              </div>
              <form role="form" className="text-start" onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="col-md-12">
                    <div className="form-group">
                      <label>ユーザー名</label>
                      <p className="form-text text-muted text-sm ms-1">
                        {workspaceUser.user_account.name} ({workspaceUser.user_account.email})
                      </p>
                    </div>
                    <div className="form-group">
                      <label>スペース管理者権限</label>
                      <div className="form-check form-switch">
                        <input type="checkbox" id="admin-check" className="form-check-input" {...register('admin')} />
                        <label className="form-check-label" htmlFor="admin-check">
                          スペース管理者にする
                        </label>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>ユーザーの無効化</label>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          id="invalidity-check"
                          className="form-check-input"
                          {...register('invalidity')}
                        />
                        <label className="form-check-label" htmlFor="invalidity-check">
                          スペースへのログインを拒否
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to={`/w/${_w_!.identifier}/setting/members`} className="btn btn-outline-secondary me-2">
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
